<script setup lang="ts">
import { computed } from 'vue'
import { VProgressCircular } from 'vuetify/components'

import type { CPLoaderVariant } from '~/portal/components/base'
import { CPTypography } from '~/portal/components/layout'

defineOptions({ name: 'CPLoader' })

const props = withDefaults(
	defineProps<{
		variant?: CPLoaderVariant
		text?: string
	}>(),
	{
		variant: 'block',
		text: undefined
	}
)

const LOADING_LABEL = 'Wird geladen'

interface CPLoaderCircle {
	size: number
	width: number
}

const CIRCLE_BY_VARIANT: Record<CPLoaderVariant, CPLoaderCircle> = {
	page: { size: 48, width: 4 },
	block: { size: 48, width: 4 },
	overlay: { size: 32, width: 4 },
	fullscreen: { size: 32, width: 4 },
	inline: { size: 24, width: 2 }
}

const circle = computed(() => CIRCLE_BY_VARIANT[props.variant])
const accessibleLabel = computed(() => props.text ?? LOADING_LABEL)
</script>

<template>
	<div
		:class="['cp-loader', `cp-loader--${variant}`]"
		role="status"
		:aria-label="accessibleLabel"
	>
		<VProgressCircular
			indeterminate
			color="primary"
			:size="circle.size"
			:width="circle.width"
		/>
		<CPTypography
			v-if="text"
			variant="body2"
			class="cp-loader__text"
			:label="text"
		/>
	</div>
</template>

<style lang="scss">
.cp-loader {
	@apply flex flex-col items-center justify-center gap-3;

	&--page {
		min-height: 80vh;
	}

	&--overlay,
	&--fullscreen {
		@apply inset-0 bg-white bg-opacity-60 z-10;
	}

	&--overlay {
		@apply absolute;
	}

	&--fullscreen {
		@apply fixed;
	}

	&--inline {
		@apply inline-flex;
	}

	&__text {
		@apply color-cp-neutral-500;
	}
}
</style>
